/**
 * One creative's cells (spec §3.3, §4.2–§4.4): its status, age, rates, Result, engagement and view rate, then the
 * labels over every creative in the page currency (§4.3). Pure; NULL is never 0.
 *
 * @module services/creatives/creativeRows
 */
import { campaignStateOf } from '@/services/channels/campaignState';
import type { CampaignState } from '@/services/channels/channelTypes';
import { ageInDays } from '@/services/insights/budgetInsightEngine';
import { fatigueDropOf, topWinnersOf, type TopCandidate } from '@/services/insights/creativeLabels';
import { resultFiguresOf, type ResultFigures } from '@/services/overview/objectiveResults';
import { addNullable, cpcOf, cpmOf, ctrOf, isLowSample, ratioOf } from '@/services/overview/overviewMath';
import type { FactRow, Totals } from '@/services/overview/overviewTypes';
import { DEFAULT_AWARENESS, type ResultMappingInfo } from '@/services/targets/resultMetric';
import { creativeFormatOf, type CreativeFormat } from './creativeFormat';
import type { CreativeAge, CreativeFact, CreativeRow } from './creativeTypes';

/** A creative whose account's currency is known: the only kind that reaches a row (R14). */
export type PricedFact = CreativeFact & { currency: string };

/**
 * R11, spec §4.4's order: the campaign ended (D2's campaignStateOf: an end date before yesterday, or completed /
 * archived) → ended with its date; the ad completed or archived → ended; the ad or the campaign paused → paused; both
 * active → active; anything else → unknown (no chip).
 */
export const creativeStateOf = (
  f: Pick<CreativeFact, 'adStatus' | 'campaignStatus' | 'campaignEndDate'>, yesterday: string,
): CampaignState => {
  const campaign = campaignStateOf({ campaignStatus: f.campaignStatus, campaignEndDate: f.campaignEndDate }, yesterday);
  if (campaign.kind === 'ended') return campaign;
  if (f.adStatus === 'completed' || f.adStatus === 'archived') return { kind: 'ended', endDate: null };
  if (f.adStatus === 'paused' || campaign.kind === 'paused') return { kind: 'paused' };
  if (f.adStatus === 'active' && campaign.kind === 'active') return { kind: 'active' };
  return { kind: 'unknown' };
};

/**
 * R10: Insights' inclusive age up to yesterday, the day the page is opened, whatever the period. "≥" when the first
 * day with impressions is the account's first day in our data; null ("—") when there is no such day by yesterday.
 */
export const creativeAgeOf = (
  f: Pick<CreativeFact, 'firstImpressionDate' | 'accountFirstDate'>, yesterday: string,
): CreativeAge | null => {
  const first = f.firstImpressionDate;
  if (first === null || first > yesterday) return null;
  return { days: ageInDays(first, yesterday), atLeast: first === f.accountFirstDate };
};

/** Reactions + comments + shares over the parts that are numbers; every part NULL → null ("ไม่ได้รายงาน"), never 0. */
export const engagementOf = (f: Pick<CreativeFact, 'reactions' | 'comments' | 'shares'>): number | null =>
  [f.reactions, f.comments, f.shares].reduce<number | null>(addNullable, null);

/** Video views ÷ impressions, for a video on Facebook or TikTok only (LINE sends completions, Google no views). */
export const viewRateOf = (
  f: Pick<CreativeFact, 'platform' | 'videoViews' | 'impressions'>, format: CreativeFormat | null,
): number | null =>
  (format === 'video' && (f.platform === 'facebook' || f.platform === 'tiktok') ? ratioOf(f.videoViews, f.impressions) : null);

/**
 * R9: D1's resultFiguresOf, unchanged, on one FactRow built from the Result window's sums. R6: a failed mapping
 * (awareness null) blanks Awareness only; the other objectives' metric never reads the awareness choice.
 */
export const creativeResultOf = (f: CreativeFact, mapping: ResultMappingInfo): ResultFigures => {
  const row: FactRow = {
    date: f.lastDate, workspaceId: f.workspaceId, platform: f.platform, objective: f.objective,
    accountId: f.accountId, accountName: null, currency: f.currency,
    spend: f.resultSpend, impressions: f.resultImpressions, clicks: f.resultClicks,
    conversions: f.resultConversions, conversionValue: null, reach: f.resultReach,
  };
  const awareness = mapping.awareness ?? (f.objective === 'awareness' ? null : DEFAULT_AWARENESS);
  return resultFiguresOf([row], f.objective, awareness);
};

/** One creative's row; `top` is false until labelledRowsOf weighs its pool. */
export const creativeRowOf = (f: CreativeFact, mapping: ResultMappingInfo, yesterday: string): CreativeRow => {
  const totals: Totals = { spend: f.spend, impressions: f.impressions, clicks: f.clicks, conversions: null, conversionValue: null, reach: null };
  const format = creativeFormatOf(f.adFormat, f.adName);
  const result = creativeResultOf(f, mapping);
  return {
    key: f.adId, workspaceId: f.workspaceId, platform: f.platform, name: f.adName, externalAdId: f.externalAdId,
    format, campaignName: f.campaignName, objective: f.objective,
    state: creativeStateOf(f, yesterday), age: creativeAgeOf(f, yesterday),
    spend: f.spend, impressions: f.impressions, clicks: f.clicks, ctr: ctrOf(totals), cpc: cpcOf(totals), cpm: cpmOf(totals),
    lowSample: isLowSample(totals),
    metric: result.metric, result: result.result, costPerResult: result.costPerResult, resultState: result.resultState,
    engagement: engagementOf(f), viewRate: viewRateOf(f, format),
    // Both halves need 500 impressions, so an n<500 creative is never Fatigue (spec §4.3).
    top: false, fatigueDrop: fatigueDropOf(f),
  };
};

const topCandidateOf = (f: PricedFact, r: CreativeRow): TopCandidate => ({
  adId: f.adId, workspaceId: f.workspaceId, platform: f.platform, objective: f.objective, currency: f.currency,
  spend: f.spend, lowSample: r.lowSample, costPerResult: r.costPerResult,
  resultImpressions: f.resultImpressions, resultClicks: f.resultClicks, resultConversions: f.resultConversions,
});

/** Every creative of the page currency as a row, with Top weighed over all of them: before any filter or sort (§4.3). */
export const labelledRowsOf = (facts: readonly PricedFact[], mapping: ResultMappingInfo, yesterday: string): CreativeRow[] => {
  const rows = facts.map((f) => creativeRowOf(f, mapping, yesterday));
  const winners = topWinnersOf(facts.map((f, i) => topCandidateOf(f, rows[i])));
  return rows.map((r) => (winners.has(r.key) ? { ...r, top: true } : r));
};
